import React, { useEffect, useState } from "react";
import { Button, Spinner, Modal, Form as BsForm } from "react-bootstrap";
import { ErrorMessage, Formik } from "formik";
import { useSelector } from "react-redux";
import {
  getTermsConditions,
  updateTermsConditions,
} from "../../services/Admin/adminServices";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import { FiEdit } from "react-icons/fi";

const Terms = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  const roleData = useSelector((state) => state.role.roles);
  const canUpdate = roleData ? roleData?.legalPagesManagement?.isUpdate : true;

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getTermsConditions();
      setInfo(response?.data?.data?.termsAndConditions || null);
    } catch (error) {
      console.error("Error fetching terms info:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdate = async (values) => {
    try {
      setSaving(true);
      await updateTermsConditions(values);
      toast.success("Updated successfully");
      fetchData();
      setShowModal(false);
    } catch (err) {
      console.error("Update failed:", err);
      toast.error(err?.response?.data?.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (info) => {
    setSelectedInfo(info);
    setShowModal(true);
  };

  return (
    <div className="container-fluid p-0">
      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-0" style={{ color: "#E5484D" }}>
            Terms & Conditions
          </h2>
        </div>

        {canUpdate && info && (
          <button
            className="btn d-inline-flex align-items-center gap-2 shadow-sm px-3 py-2 text-white fw-medium"
            style={{
              backgroundColor: "#E5484D",
              borderColor: "#E5484D",
              borderRadius: "0.5rem",
            }}
            onClick={() => handleEditClick(info)}
          >
            <FiEdit size={16} /> Edit Content
          </button>
        )}
      </div>

      {/* Content Container */}
      {loading ? (
        <div
          className="card border-0 shadow-sm p-5 text-center my-4"
          style={{ borderRadius: "1rem", backgroundColor: "#fff" }}
        >
          <div className="py-4">
            <Spinner animation="border" style={{ color: "#E5484D" }} role="status" />
            <p className="mt-3 text-muted mb-0">Loading Terms & Conditions...</p>
          </div>
        </div>
      ) : !info ? (
        <div
          className="card border-0 shadow-sm p-5 text-center my-4"
          style={{ borderRadius: "1rem", backgroundColor: "#fff" }}
        >
          <div className="py-4">
            <p className="text-muted mb-0">No Terms & Conditions content available.</p>
          </div>
        </div>
      ) : (
        <div
          className="card border-0 shadow-sm overflow-hidden"
          style={{ borderRadius: "1rem", backgroundColor: "#fff" }}
        >
          <div className="card-body p-4 p-md-5">
            <div
              className="terms-content-preview"
              style={{
                fontSize: "15px",
                lineHeight: "1.8",
                color: "#2D3748",
              }}
              dangerouslySetInnerHTML={{ __html: info }}
            />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {selectedInfo && (
        <Modal
          size="xl"
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
        >
          <Modal.Header closeButton style={{ backgroundColor: "#FFE4E6" }}>
            <Modal.Title style={{ color: "#E5484D" }}>
              Edit Terms & Conditions
            </Modal.Title>
          </Modal.Header>
          <Formik
            initialValues={{
              termsAndConditions: selectedInfo || "",
            }}
            enableReinitialize
            onSubmit={handleUpdate}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <BsForm onSubmit={handleSubmit}>
                <Modal.Body>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Description</label>

                    <ReactQuill
                      value={values.termsAndConditions}
                      theme="snow"
                      name="description"
                      onChange={(content) =>
                        setFieldValue("termsAndConditions", content)
                      }
                      style={{
                        height: "250px",
                        marginBottom: "50px",
                      }}
                    />
                    <ErrorMessage
                      name="termsAndConditions"
                      component="div"
                      className="text-danger mt-1 small"
                    />
                  </div>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: "#FFE4E6" }}>
                  <Button
                    variant="secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={saving}
                    type="submit"
                    style={{
                      backgroundColor: "#E5484D",
                      borderColor: "#E5484D",
                      color: "white",
                    }}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </Modal.Footer>
              </BsForm>
            )}
          </Formik>
        </Modal>
      )}

      {/* Scoped CSS for Rich Content styling */}
      <style>{`
        .terms-content-preview h1,
        .terms-content-preview h2,
        .terms-content-preview h3,
        .terms-content-preview h4 {
          color: #1A202C;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .terms-content-preview h1 { font-size: 1.75rem; }
        .terms-content-preview h2 { font-size: 1.5rem; }
        .terms-content-preview h3 { font-size: 1.25rem; }
        .terms-content-preview p {
          margin-bottom: 1.25rem;
        }
        .terms-content-preview ul,
        .terms-content-preview ol {
          padding-left: 1.5rem;
          margin-bottom: 1.25rem;
        }
        .terms-content-preview li {
          margin-bottom: 0.5rem;
        }
        .terms-content-preview img {
          max-width: 100%;
          height: auto;
          border-radius: 0.75rem;
          margin: 1rem 0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .terms-content-preview blockquote {
          border-left: 4px solid #E5484D;
          padding: 0.75rem 1rem;
          margin-left: 0;
          color: #4A5568;
          font-style: italic;
          background: #FFF5F5;
          border-radius: 0 0.5rem 0.5rem 0;
          margin-bottom: 1.25rem;
        }
      `}</style>
    </div>
  );
};

export default Terms;
